import type { Provider } from '../../types/provider';
import { formatNumber } from '../../lib/utils';
import { PROVIDER_NAME_COL_MAX_CLASS } from './provider-name-col';

interface ProviderRowProps {
  readonly provider: Provider;
  /**
   * Optional row-click handler. When passed, the row becomes
   * keyboard-activatable (cursor-pointer, aria-haspopup=dialog,
   * Enter/Space fires the same handler). Mirrors the same pattern
   * RankingsRow uses for the RowPeekDrawer wiring.
   */
  readonly onSelect?: (provider: Provider) => void;
  /**
   * IMP-005 — when the user has filtered the Providers page by a
   * specific occupational group, the row's "Programs" cell drills
   * down to ONLY that group's programs (first program + "+N more"
   * pill reflects the filtered count). When null/undefined, the
   * existing behaviour is preserved (full top-programs list, first
   * program + "+N more" across all OGs).
   *
   * The "All OGs" branch -- where with no filter the cell should
   * surface every springboard-aligned program rather than a
   * top-N slice -- is deferred until the source data carries
   * per-institution total program counts (EXT-001) so we can avoid
   * double-counting.
   */
  readonly activeOgName?: string | null;
}

/**
 * One row in the providers table. Columns mirror the
 * <ProvidersTableView> header set:
 *
 *   1. Name                — sticky left (frozen-edge shadow)
 *   2. Metro               — secondary text (metroName)
 *   3. Total awards        — integer-formatted (.toLocaleString)
 *   4. Springboard occupational groups — count + tooltip listing names
 *   5. Programs            — first program + "+N more" pill
 *   6. Providers share of credentials aligned with springboard
 *      occupations        — percentage; "—" for null
 *
 * The hover state, mobile card layout, and data-label affordances
 * all reuse the `providers-table` CSS rules in `styles/globals.css`,
 * which mirror the `rankings-table` rules.
 */
export function ProviderRow({ provider, onSelect, activeOgName }: ProviderRowProps) {
  // CR-01 (2026-07-07, Maria msg2 #1): a prior version of this cell
  // always showed the institution TOTAL when an OG filter was active,
  // because the backend didn't yet expose a real per-group figure
  // (code-review finding #5, LUIS-BATCH-110-116-REVIEW.md). The
  // backend now emits `awardsByOccGroup` -- the authoritative FULL
  // per-occupational-group award total (NOT the truncated top-5
  // `topPrograms` sum). When an OG filter is active, this cell now
  // reads the real per-group figure; with no filter it still shows
  // the institution total.
  const displayedAwards = Math.round(
    activeOgName ? (provider.awardsByOccGroup[activeOgName] ?? 0) : provider.totalAwards,
  );
  const groupsCount = provider.occupationGroups.length;
  const groupsTooltip = provider.occupationGroups.join('\n');
  // CR-015: the "Springboard occupation-aligned programs" count.
  //   - OG filter active: that group's programs (top-N per group).
  //   - No filter ("All OGs"): the DEDUPLICATED `programs` list — each
  //     program once. This closes the old EXT-001 double-count (the "All
  //     OGs" branch used to count `topPrograms`, which repeats a multi-group
  //     program per group and truncates at 5/group); Georgetown's
  //     instn_cip_tag now gives the correct distinct set.
  const displayPrograms = activeOgName
    ? provider.topPrograms.filter((p) => p.occGroup === activeOgName)
    : provider.programs;
  // CR-008: read the share for the ACTIVE occupational group, not a
  // collapsed scalar. The cell only renders under an active OG filter
  // (below), so with no filter there is nothing to show. A group whose
  // source share was invalid/absent is missing from the map -> em-dash.
  // Mirrors the awardsByOccGroup[activeOgName] pattern above.
  const sbShareRaw =
    activeOgName != null ? provider.shareSbByOccGroup[activeOgName] : undefined;
  const sbSharePct = sbShareRaw == null ? null : Math.round(sbShareRaw * 100);

  return (
    <tr
      className={`group border-b border-border-light text-sm transition-colors hover:bg-ice ${
        onSelect ? 'cursor-pointer focus-within:bg-ice' : ''
      }`}
      // <tr> onClick = mouse-only convenience; the accessible control is the
      // real <button> in the Provider cell below (keyboard + AT + aria-haspopup).
      onClick={onSelect ? () => onSelect(provider) : undefined}
    >
      <td
        data-label="Provider"
        className={`sticky left-0 z-[1] ${PROVIDER_NAME_COL_MAX_CLASS} bg-white px-3 py-2.5 group-hover:bg-ice`}
      >
        {/* WCAG 1.3.1 / 4.1.2 (Siteimprove/Alfa sia-r46): interactive affordance
            is a real <button> INSIDE the cell, not role="button" ON the <td>
            (which strips the cell role and unassigns the column header).
            stopPropagation so the button click doesn't double-fire the row. */}
        {onSelect ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(provider);
            }}
            aria-haspopup="dialog"
            className="block max-w-full truncate text-left font-medium text-text-1"
          >
            {provider.name}
          </button>
        ) : (
          <span className="block max-w-full truncate font-medium text-text-1">{provider.name}</span>
        )}
      </td>
      {/* 2026-07-02 (V2 feedback): data-label strings updated to match
          the new column headers (see ProvidersTableView COLUMNS).
          These labels drive the mobile card layout's caption prefix
          via CSS ::before, so keeping them in sync with the header
          is what makes the mobile view read correctly. */}
      <td
        data-label="Metro area"
        className="max-w-[200px] truncate px-3 py-2.5 text-text-3"
      >
        {provider.metroName}
      </td>
      {/* CR-01 (2026-07-07): this cell switches data source based on
          the OG filter:
            - No OG selected  -> provider.totalAwards      (institution-wide)
            - OG selected     -> provider.awardsByOccGroup[activeOgName]
              (the authoritative FULL per-group total, not a topPrograms
              proxy -- see the field's JSDoc in `src/types/provider.ts`). */}
      <td
        data-label="Annual credentials awarded"
        className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
      >
        {formatNumber(displayedAwards)}
      </td>
      {/* 2026-06-05 (Maria): only render the OG-count cell when NO
          specific OG filter is active. The count is a breadth signal
          across the full set; once a single OG is in scope it's
          always 1 and adds no information. The matching column
          header in ProvidersTableView is gated by the same check. */}
      {!activeOgName ? (
        <td
          data-label="Springboard occupational groups"
          className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
          title={groupsTooltip}
        >
          {groupsCount}
        </td>
      ) : null}
      {/* Programs cell — 2026-06-05 (Maria) simplified: shows just the
          count of aligned programs. The full list lives in the
          row-peek modal that opens when the user clicks the row
          (the column header carries a "click to see detailed view"
          note so the affordance is discoverable). The previous
          "first program + '+N more' pill" pattern was dropped
          because the title alone wasn't enough context, and the
          modal now exists to surface the full list. */}
      <td
        data-label="Springboard occupation-aligned programs"
        className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
      >
        {displayPrograms.length > 0 ? (
          displayPrograms.length
        ) : (
          <span className="text-text-4">—</span>
        )}
      </td>
      {/* 2026-06-05 (Maria): only render the springboard-share cell
          when an OG filter is active. The cross-OG share is hard to
          interpret without a single anchor group, so hiding it under
          "All occupational groups" keeps the table focused. The
          matching column header in ProvidersTableView is gated by
          the same `activeOgName` check. */}
      {activeOgName ? (
        <td
          data-label="Provider's production share of credentials aligned with springboard occupations"
          className="px-3 py-2.5 text-left font-mono tabular-nums text-text-2"
        >
          {sbSharePct === null ? <span className="text-text-4">—</span> : `${sbSharePct}%`}
        </td>
      ) : null}
    </tr>
  );
}
